import { useCallback, useMemo, useState } from 'react';
import {
  Button as AriaButton,
  Dialog,
  DialogTrigger,
} from 'react-aria-components';

import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { Popover } from '@/lib/base/overlays/popover';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import { unwrapPropertyValue } from '@/types/crm';

import { PropertyValueLabel } from './PropertyValueLabel';

export interface ExpandableMultiValueLabelProps {
  value: unknown;
  propertyType: PropertyTypeSchema | undefined;
  orgId?: number;
  fontSize?: 'xs' | 'table' | 'sm' | 'base' | 'lg';
  /** CSS truncation with ellipsis for the always-visible portion (the
   * delegate single value, or the first value ahead of "+N"). Forwarded to
   * {@link PropertyValueLabel} — required wherever the host has a fixed
   * row height (e.g. a table cell) and cannot grow to fit a long value. */
  truncate?: boolean;
  /**
   * The entity's raw `properties` map. Only meaningful when the value is a
   * `formula` field — forwarded to {@link PropertyValueLabel} so its target
   * entity type (named by a sibling property) can be resolved. See
   * `PropertyValueLabelProps.entityProperties`.
   */
  entityProperties?: Record<string, unknown>;
  /**
   * 'inline' (default): expand in place below the value plus a "Show less"
   * toggle — the original merge-wizard behavior, also right for the record
   * page's overview rows, which can grow. Used by `overview-section.tsx`,
   * which relies on the default.
   * 'popover': render the value list in a floating popover anchored to the
   * "+N" trigger instead, leaving the host's height untouched — required
   * for a table cell, whose row has a fixed height that in-place expansion
   * would fight. Used by `editable-cell.tsx`, which passes `'popover'`
   * explicitly.
   */
  mode?: 'inline' | 'popover';
}

function keyFor(item: unknown): string {
  return typeof item === 'string' ? item : JSON.stringify(item);
}

/**
 * Stops the "+N" trigger's click/keydown from reaching an editable host.
 *
 * Every host that treats its read value as a click(/Enter/Space)-to-edit
 * region would otherwise read a press on "+N" as "start editing" instead of
 * "expand" — InlineEditableFieldRow wraps the whole value in a
 * `role="button"` div with `onClick`/Enter-Space `onKeyDown`
 * (overview-section.tsx), and DataTable's editable cell has its own
 * click-to-select / dblclick-to-edit / F2-Enter-to-edit handlers
 * (editable-cell.tsx). "+N" renders as a real `<button>`, which
 * InlineEditableFieldRow's own `useHasOwnInteractiveValue` check already
 * detects and excludes from its row-level click wrapper — rendering the
 * Edit pencil beside the value instead of wrapping the value in
 * `role="button"`, so that host never nests a button inside one. Calling
 * stopPropagation here is what keeps "click +N without editing" true
 * generically, including for hosts (like the table cell) that have no such
 * detection and would otherwise still receive the bubbled event.
 */
function stopEventPropagation(e: { stopPropagation: () => void }): void {
  e.stopPropagation();
}

/**
 * Renders a property value via {@link PropertyValueLabel}, but for multi-value
 * properties shows "first value + N more" where the "+N" is a clickable button
 * that reveals every value — either expanded in place ('inline') or in a
 * popover ('popover'), see {@link ExpandableMultiValueLabelProps.mode}.
 *
 * For single / non-multi values it delegates straight to PropertyValueLabel, so
 * it is a drop-in replacement anywhere a reviewable, expandable value is wanted.
 */
export function ExpandableMultiValueLabel({
  value,
  propertyType,
  orgId,
  fontSize = 'sm',
  truncate,
  entityProperties,
  mode = 'inline',
}: ExpandableMultiValueLabelProps) {
  const [expanded, setExpanded] = useState(false);

  const handleExpand = useCallback((e: { stopPropagation: () => void }) => {
    stopEventPropagation(e);
    setExpanded(true);
  }, []);
  const handleCollapse = useCallback((e: { stopPropagation: () => void }) => {
    stopEventPropagation(e);
    setExpanded(false);
  }, []);

  // property value: single-level {value} wrapper — arrays pass through untouched;
  // non-arrays are unwrapped one level via unwrapPropertyValue.
  const unwrapped: unknown = unwrapPropertyValue(value);

  const items =
    propertyType?.is_multi && Array.isArray(unwrapped)
      ? unwrapped.filter(v => v !== null && v !== undefined && v !== '')
      : [];

  const singleType = useMemo<PropertyTypeSchema | undefined>(
    () => (propertyType ? { ...propertyType, is_multi: false } : undefined),
    [propertyType]
  );

  // Not an expandable multi-value (single item, non-multi, empty) — delegate.
  if (items.length <= 1) {
    return (
      <PropertyValueLabel
        value={value}
        propertyType={propertyType}
        orgId={orgId}
        fontSize={fontSize}
        truncate={truncate}
        entityProperties={entityProperties}
      />
    );
  }

  if (mode === 'inline' && expanded) {
    return (
      <span className='inline-flex flex-wrap items-center gap-x-1 gap-y-0.5'>
        {items.map((item, i) => (
          <span key={keyFor(item)} className='inline-flex items-center'>
            <PropertyValueLabel
              value={item}
              propertyType={singleType}
              orgId={orgId}
              fontSize={fontSize}
              entityProperties={entityProperties}
            />
            {i < items.length - 1 && (
              <span className='text-[var(--color-text-tertiary)]'>,</span>
            )}
          </span>
        ))}
        <Button
          color='link-gray'
          size='sm'
          onClick={handleCollapse}
          onKeyDown={stopEventPropagation}
        >
          Show less
        </Button>
      </span>
    );
  }

  const remaining = items.length - 1;
  const overflowLabel = `Show ${remaining} more`;

  const firstValue = (
    <span className='min-w-0 flex-1'>
      <PropertyValueLabel
        value={items[0]}
        propertyType={singleType}
        orgId={orgId}
        fontSize={fontSize}
        truncate={truncate}
        entityProperties={entityProperties}
      />
    </span>
  );

  if (mode === 'popover') {
    return (
      <span className='inline-flex max-w-full min-w-0 items-center gap-1'>
        {firstValue}
        {/* Clickable "+N" pill — opens a popover listing every value instead
            of expanding in place, so a fixed-height table row never grows. */}
        <DialogTrigger>
          <AriaButton
            aria-label={overflowLabel}
            className='shrink-0 cursor-pointer rounded-sm outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand-600)]'
            onClick={stopEventPropagation}
            onKeyDown={stopEventPropagation}
          >
            <Badge color='gray' size='sm'>
              {`+${remaining}`}
            </Badge>
          </AriaButton>
          <Popover size='sm' placement='bottom start'>
            <Dialog
              aria-label={`${remaining} more value${remaining === 1 ? '' : 's'}`}
              className='flex max-w-72 min-w-40 flex-col gap-1 p-2 outline-none'
            >
              {items.map(item => (
                <div key={keyFor(item)} className='px-1 py-0.5'>
                  <PropertyValueLabel
                    value={item}
                    propertyType={singleType}
                    orgId={orgId}
                    fontSize={fontSize}
                    entityProperties={entityProperties}
                  />
                </div>
              ))}
            </Dialog>
          </Popover>
        </DialogTrigger>
      </span>
    );
  }

  return (
    <span className='inline-flex max-w-full min-w-0 items-center gap-1'>
      {firstValue}
      {/* Clickable "+N" pill — same gray badge PropertyValueLabel shows for
          multi-values, but click expands to reveal every value (mirrors the
          EntityBadgeGroup overflow pill). */}
      <Button
        color='link-gray'
        size='sm'
        noTextPadding
        onClick={handleExpand}
        onKeyDown={stopEventPropagation}
        aria-label={overflowLabel}
        className='shrink-0'
      >
        <Badge color='gray' size='sm'>
          {`+${remaining}`}
        </Badge>
      </Button>
    </span>
  );
}
